// ─── Settings → API Keys ─────────────────────────────────────────────────────
// Lists every active Apikey row for the current project. Same actions the
// legacy Symfony UI offered: Generate New Key, Edit (name + allow_ip),
// Remove (soft-delete via active=0), Refresh. The "API Calls (hourly): N/240"
// header counter polls the stats endpoint and refreshes alongside the list.
//
// Generate and Edit each open in their own modal (matching the XD design)
// rather than editing inline — the Edit modal additionally exposes the
// 32-char Key as a read-only field at the top so the user can copy it out.

import { useEffect, useState } from 'react'
import { Loader, AlertCircle, X } from 'lucide-react'
import {
    listApiKeys, createApiKey, updateApiKey, deleteApiKey, getApiKeyStats,
} from '../../../lib/settingsApi'
import ApiDocsSection from './ApiDocsSection'

const fmtDate = (s) => {
    if (!s) return ''
    const d = new Date(s)
    if (isNaN(d.getTime())) return String(s)
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function ApiKeysPanel({ projectId }) {
    const [rows,    setRows]    = useState([])
    const [stats,   setStats]   = useState({ count: 0, limit: 240 })
    const [loading, setLoading] = useState(false)
    const [error,   setError]   = useState(null)
    // Two modals: `dialog` is null when nothing's open, { mode: 'create' }
    // for generate-new, { mode: 'edit', row } for editing an existing key.
    const [dialog,  setDialog]  = useState(null)

    const load = async () => {
        if (!projectId) return
        setLoading(true); setError(null)
        try {
            const [list, st] = await Promise.all([
                listApiKeys(projectId).catch(() => []),
                getApiKeyStats(projectId).catch(() => ({ count: 0, limit: 240 })),
            ])
            setRows(list)
            setStats({ count: st?.count ?? 0, limit: st?.limit ?? 240 })
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || 'Failed to load API keys')
        } finally {
            setLoading(false)
        }
    }
    useEffect(() => { load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [projectId])

    const onRemove = async (row) => {
        if (!window.confirm(`Remove API key "${row.name || row.id}"? Calls using this key will start failing immediately.`)) return
        try {
            await deleteApiKey(row.id)
            await load()
        } catch (e) {
            alert(e?.response?.data?.message || e?.message || 'Failed to remove API key')
        }
    }

    return (
        // Wrap the two cards in a vertical stack so the Settings tab's
        // outer `flex justify-center` doesn't put them side-by-side.
        <div className="flex flex-col items-center gap-6 w-full">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 w-full max-w-5xl">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="common-size18 text-lg font-bold text-[#4D4D4D]">API Keys</h2>
                    <span className="common-size15 text-sm text-[#4D4D4D]">
                        API Calls (hourly): <span className="text-[#4D4D4D]">{stats.count}/{stats.limit}</span>
                    </span>
                </div>

                {error && (
                    <div className="common-size15 mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> {error}
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-xs text-gray-600">
                            <tr className="border-b border-gray-200">
                                <th className="common-size15 text-[#4D4D4D] text-left font-semibold py-4 pr-3 w-10">#</th>
                                <th className="common-size15 text-[#4D4D4D] text-left font-semibold py-4 pr-3">Name</th>
                                {/* "Email Address" is the legacy Symfony column label — actually
                                    holds the 32-char secret_key. Kept for UI parity. */}
                                <th className="common-size15 text-[#4D4D4D] text-left font-semibold py-4 pr-3">Email Address</th>
                                <th className="common-size15 text-[#4D4D4D] text-left font-semibold py-4 pr-3">Allow IP</th>
                                <th className="common-size15 text-[#4D4D4D] text-left font-semibold py-4 pr-3">Created At</th>
                                <th className="common-size15 text-[#4D4D4D] text-left font-semibold py-4">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={6} className="py-6 text-center text-gray-500 text-sm">
                                    <Loader className="inline w-4 h-4 animate-spin mr-1" /> Loading…
                                </td></tr>
                            )}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={6} className="py-6 text-center text-gray-400 italic text-sm">
                                    No API keys yet. Click <span className="">Generate New Key</span> to make one.
                                </td></tr>
                            )}
                            {rows.map((r, i) => (
                                <tr key={r.id} className="border-b border-gray-100">
                                    <td className="common-size15 text-[#4D4D4D] py-4 pr-3 text-gray-600">{i + 1}</td>
                                    <td className="common-size15 text-[#4D4D4D] py-4 pr-3 text-gray-800">{r.name || '—'}</td>
                                    <td className="common-size15 text-[#4D4D4D] py-4 pr-3 font-mono text-xs text-gray-700 break-all max-w-[260px]">{r.secret_key}</td>
                                    <td className="common-size15 text-[#4D4D4D] py-4 pr-3 text-gray-700">{r.allow_ip || ''}</td>
                                    <td className="common-size15 text-[#4D4D4D] py-4 pr-3 text-gray-700">{fmtDate(r.created_at)}</td>
                                    <td className="common-size15 text-[#4D4D4D] py-4">
                                        <div className="flex items-center gap-3">
                                            <button onClick={() => onRemove(r)}
                                                    className="common-size15 text-[#EA5454] hover:text-red-700 underline text-sm ">
                                                Remove
                                            </button>
                                            <button onClick={() => setDialog({ mode: 'edit', row: r })}
                                                    className="common-size15 text-[#4D4D4D] underline text-sm">
                                                Edit
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center justify-end gap-3 mt-4">
                    <button onClick={() => setDialog({ mode: 'create' })}
                            className="text-sm md:common-size18 px-5 py-1.5 border-2 border-[#358ECD] text-[#358ECD] uppercase font-bold text-sm rounded-md transition">
                        GENERATE NEW KEY
                    </button>
                    <button onClick={load} disabled={loading}
                            className="text-sm md:common-size18 px-5 py-1.5 border-2 border-[#358ECD] text-[#358ECD] uppercase font-bold text-sm rounded-md transition disabled:opacity-50 inline-flex items-center gap-1">
                        {loading && <Loader className="w-3.5 h-3.5 animate-spin" />}
                        REFRESH
                    </button>
                </div>
            </div>

            {/* Docs render once the project has at least one active API key
                (per legacy Symfony behaviour) — no keys means the endpoints
                can't be exercised anyway, so the docs stay hidden. */}
            {!loading && rows.length > 0 && <ApiDocsSection />}

            {dialog && (
                <ApiKeyDialog
                    mode={dialog.mode}
                    row={dialog.row}
                    projectId={projectId}
                    onClose={() => setDialog(null)}
                    onSaved={async () => { setDialog(null); await load() }}
                />
            )}
        </div>
    )
}

// ─── Generate / Edit dialog ─────────────────────────────────────────────────
// Same form shape for both modes. Edit additionally shows the read-only
// 32-char `Key` field at the top so users can copy the secret out.
function ApiKeyDialog({ mode, row, projectId, onClose, onSaved }) {
    const isEdit = mode === 'edit'
    const [name,    setName]    = useState(isEdit ? (row?.name || '')     : '')
    const [allowIp, setAllowIp] = useState(isEdit ? (row?.allow_ip || '') : '')
    const [saving,  setSaving]  = useState(false)
    const [error,   setError]   = useState(null)

    const submit = async (e) => {
        e?.preventDefault?.()
        const n = name.trim()
        if (!n) { setError('Name is required'); return }
        setSaving(true); setError(null)
        try {
            if (isEdit) {
                await updateApiKey(row.id, { name: n, allowIp: allowIp.trim() })
            } else {
                await createApiKey({ projectId, name: n, allowIp: allowIp.trim() })
            }
            onSaved?.()
        } catch (e2) {
            setError(e2?.response?.data?.message || e2?.message || 'Failed to save')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-2xl w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">
                        {isEdit ? 'Edit Key' : 'Generate New Key'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <form onSubmit={submit} className="px-5 py-4 space-y-4">
                    {error && (
                        <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" /> {error}
                        </div>
                    )}

                    {isEdit && (
                        <label className="block">
                            <span className="common-size15 block font-semibold text-sm text-[#4D4D4D] mb-1.5">Key</span>
                            <input
                                type="text"
                                value={row?.secret_key || ''}
                                readOnly
                                onFocus={(e) => e.target.select()}
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono text-gray-600 bg-gray-50"
                            />
                        </label>
                    )}

                    <label className="block">
                        <span className="common-size15 block text-sm font-semibold text-[#4D4D4D] mb-1.5">Name</span>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus={!isEdit}
                            placeholder=""
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
                        />
                    </label>

                    <label className="block">
                        <span className="common-size15 block text-sm font-semibold text-[#4D4D4D] mb-1.5">Allow IP</span>
                        <input
                            type="text"
                            value={allowIp}
                            onChange={(e) => setAllowIp(e.target.value)}
                            placeholder=""
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
                        />
                        <p className="common-size12 mt-1 text-xs text-[#4D4D4D]">Only one IPv4 or IPv6. Leave empty for all.</p>
                    </label>

                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full py-2.5 bg-[#358ECD] hover:bg-[#2A78B0] text-white text-sm font-bold tracking-wider rounded disabled:opacity-50 inline-flex items-center justify-center gap-1"
                    >
                        {saving && <Loader className="w-3.5 h-3.5 animate-spin" />}
                        SAVE
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="block mx-auto text-sm text-[#358ECD] hover:underline disabled:opacity-50"
                    >
                        Cancel
                    </button>
                </form>
            </div>
        </div>
    )
}
